import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { CButton, CButtonGroup } from '@coreui/react-pro'
import './PanelChargesCard.scss'
import PanelChargesSearchBox from './PanelChargesSearchBox'
import PanelChargesDisplay from './PanelChargesDisplay'
import {
  addCPTVerification,
  cptSearch,
  updateCPTWithFSModifier,
} from '../../../../../components/common/shared/sharedCPTLogic'
import PanelSelectedChargesDisplay from './PanelSelectedChargesDisplay'
import PanelChargeEmDisplay from './PanelChargeEmDisplay'
import AiCodeCheckSpinner from '../sharedPanelComponents/AiCodeCheckSpinner'
import { saveEncounterField } from '../../../../../redux/sidePanel/sidePanelThunk'
import PanelMDMCard from './PanelMDMCard'
import { debouncer } from '../sharedPanelComponents/sharedAIfunctions'
import { setEncounterField } from '../../../../../redux/sidePanel/sidePanelSlice'
import { getVisitProcedures } from '../../../../../helper/backendHelper'
import FEATURES from '../../../../../utils/featuresConstants'

const CPT_TABS = {
  SELECTED: 'SELECTED',
  SUGGESTED: 'SUGGESTED',
  PREVIOUS: 'PREVIOUS',
  SEARCH: 'SEARCH',
}

export default function PanelChargesCard() {
  const dispatch = useDispatch()

  const provider = useSelector((state) => state?.user?.provider)
  const practice = useSelector((state) => state?.user?.practice)
  const roles = useSelector((state) => state?.user?.roles) || []
  const features = useSelector((state) => state?.user?.features) || []

  const cptCodes = useSelector((state) => state.sidePanel.panelEncounter?.procedureCodes)
  const encounterId = useSelector((state) => state.sidePanel.panelEncounter?._id)
  const encounterType = useSelector((state) => state.sidePanel.panelEncounter?.encounterType)
  const placeOfService = useSelector((state) => state.sidePanel.panelEncounter?.place_of_service)
  const isReadOnly = useSelector((state) => state.sidePanel.isReadOnly)
  const isLastEncounter = useSelector((state) => state.sidePanel.isLastEncounter)
  const selectedSharedProvider = useSelector(
    (state) => state.sidePanel.panelEncounter?.incident_to_provider_id,
  )
  const encDOS = useSelector((state) => state.sidePanel.panelEncounter?.service_date_from)
  const mdm = useSelector(
    (state) => state.sidePanel.panelEncounter?.coding_ai?.anthropic?.mdmelements,
  )

  const allVisits = useSelector((state) => state.sidePanel.panelEncHistory)
  const aiCodeCheckFiring = useSelector((state) => state.sidePanel.aiCodeCheckFiring)

  const [cptTab, setCptTab] = useState(CPT_TABS.SELECTED)

  const [suggestedCPTs, setSuggestedCPTs] = useState([])
  const [cptHistory, setCptHistory] = useState([])

  let emCode = null
  let regularCPTs = cptCodes
  if (cptCodes?.[0]?.em_code) {
    emCode = cptCodes[0]
    regularCPTs = regularCPTs.slice(1)
  }

  useEffect(() => {
    const cptHistoryMap = {}
    const history = []
    allVisits.forEach(({ procedureCodes, service_date_from }) => {
      if (service_date_from === encDOS) {
        return
      }
      const uniqueCPTs = []
      procedureCodes?.forEach((cpt) => {
        if (!cptHistoryMap[cpt.code]) {
          cptHistoryMap[cpt.code] = cpt.code
          uniqueCPTs.push(cpt)
        }
      })
      if (uniqueCPTs.length > 0) {
        history.push({
          date: service_date_from,
          cpts: uniqueCPTs,
        })
      }
    })
    setCptHistory(history)
    // experimental
  }, [allVisits?.length])

  const searchData = async (placeOfService, encounterType) => {
    const newCPTs = await cptSearch({
      searchString: '',
      pos: placeOfService,
      encounterType: encounterType,
    })

    let searchedResult = (newCPTs || []).toSorted((a, b) => {
      if (a.initial_code && !b.initial_code) {
        return encounterType === 'Initial' ? -1 : 1
      } else if (!a.initial_code && b.initial_code) {
        return encounterType === 'Initial' ? 1 : -1
      }

      return 0
    })

    setSuggestedCPTs(searchedResult)
  }

  useEffect(() => {
    searchData(placeOfService, encounterType)
  }, [placeOfService, encounterType])

  useEffect(() => {
    if (features.includes(FEATURES.SuppressCPTSelection)) {
      refreshCPTs()
    }
  }, [features, mdm])

  useEffect(() => {
    if (!isLastEncounter) {
      updateCPTWithFSModifier(selectedSharedProvider, cptCodes, (newCPTs) =>
        updateSelectedCPT(newCPTs),
      )
    }
  }, [dispatch, selectedSharedProvider, cptCodes, isLastEncounter])

  const updateSelectedCPT = (newCPTs) => {
    dispatch(
      setEncounterField({
        field: 'procedureCodes',
        value: newCPTs,
      }),
    )
    debouncer(() => dispatch(saveEncounterField({})))
  }

  const refreshCPTs = async () => {
    const res = await getVisitProcedures(encounterId)
    updateSelectedCPT(res?.results || [])
  }

  const onCptToggle = (item) => {
    if (!cptCodes.find((cpt) => cpt.code === item.code)) {
      addCPTVerification(cptCodes, item, updateSelectedCPT)
    } else {
      updateSelectedCPT(cptCodes.filter((cpt) => cpt.code !== item.code))
    }
  }

  return (
    <div id={'PanelChargesCard'} className={'d-flex flex-column gap-2'}>
      {practice?.medical_coding &&
      provider?.medical_coding !== false &&
      !roles.includes('Claimocity App Biller') ? (
        <div className="pt-3">This visit will be submitted to the coding team</div>
      ) : (
        <>
          <PanelMDMCard />

          <CButtonGroup>
            <CButton
              color={cptTab === CPT_TABS.SELECTED ? 'primary' : ''}
              onClick={() => setCptTab(CPT_TABS.SELECTED)}
            >
              Selected
            </CButton>
            <CButton
              color={cptTab === CPT_TABS.SUGGESTED ? 'primary' : ''}
              onClick={() => setCptTab(CPT_TABS.SUGGESTED)}
            >
              Level Coding
            </CButton>
            <CButton
              color={cptTab === CPT_TABS.PREVIOUS ? 'primary' : ''}
              onClick={() => setCptTab(CPT_TABS.PREVIOUS)}
            >
              Billing HX
            </CButton>
            {!(isReadOnly && !isLastEncounter) && (
              <CButton
                color={cptTab === CPT_TABS.SEARCH ? 'primary' : ''}
                onClick={() => setCptTab(CPT_TABS.SEARCH)}
              >
                Search
              </CButton>
            )}
          </CButtonGroup>
          {cptTab === CPT_TABS.SELECTED && (
            <>
              {aiCodeCheckFiring ? (
                <div className={'aiSpinner'} style={{ marginTop: '6em' }}>
                  <AiCodeCheckSpinner />
                </div>
              ) : (
                <>
                  <PanelChargeEmDisplay
                    disabled={isReadOnly && !isLastEncounter}
                    emCode={emCode}
                    setSelectedCPT={(emCodeArr) => {
                      updateSelectedCPT([...emCodeArr, ...regularCPTs])
                    }}
                  />
                  <div
                    className={
                      'regularCptWrapper display-box ' + (regularCPTs?.length === 0 ? 'empty' : '')
                    }
                  >
                    <div className={'boxTitle'}>Procedures</div>
                    <PanelSelectedChargesDisplay
                      selectedCPT={regularCPTs}
                      setSelectedCPT={(newRegCPTs) => {
                        let newCPTs = emCode ? [emCode] : []
                        updateSelectedCPT([...newCPTs, ...newRegCPTs])
                      }}
                    />
                  </div>
                </>
              )}
            </>
          )}
          {cptTab === CPT_TABS.SUGGESTED && (
            <PanelChargesDisplay cptTabList={suggestedCPTs} onCPTToggle={onCptToggle} />
          )}
          {cptTab === CPT_TABS.PREVIOUS && (
            <PanelChargesDisplay cptTabList={cptHistory} onCPTToggle={onCptToggle} isHistory />
          )}
          {cptTab === CPT_TABS.SEARCH && !(isReadOnly && !isLastEncounter) && (
            <PanelChargesSearchBox onCptToggle={onCptToggle} />
          )}
        </>
      )}
    </div>
  )
}
